import React, { useState, useEffect } from 'react';
import { Building2, Mail, Phone, MapPin, Edit2, Save, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { fetchWithAuth } from '../../utils/authUtils';

interface ProfileData {
  companyName: string;
  rut: string;
  address: string;
  phoneNumber: string;
  email: string;
  contactName: string;
  contactPosition: string;
  contactPhone: string;
  contactEmail: string;
  active: boolean;
  updatedAt: string | null;
}

const empty: ProfileData = {
  companyName: '',
  rut: '',
  address: '',
  phoneNumber: '',
  email: '',
  contactName: '',
  contactPosition: '',
  contactPhone: '',
  contactEmail: '',
  active: true,
  updatedAt: null,
};

const ProfileSection: React.FC = () => {
  const [isEditing, setIsEditing]     = useState(false);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [saveError, setSaveError]     = useState('');
  const [profileData, setProfileData] = useState<ProfileData>(empty);
  const [editedData, setEditedData]   = useState<ProfileData>(empty);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProfile = async () => {
      try {
        const res  = await fetchWithAuth(API_ENDPOINTS.profile, { signal: controller.signal });
        const data = await res.json();
        if (res.ok && data.success) {
          const u = data.user;
          const mapped: ProfileData = {
            companyName:     u.companyName     || '',
            rut:             u.rut             || '',
            address:         u.address         || '',
            phoneNumber:     u.phone           || '',
            email:           u.email           || '',
            contactName:     u.contactName     || '',
            contactPosition: u.contactPosition || '',
            contactPhone:    u.contactPhone    || '',
            contactEmail:    u.contactEmail    || '',
            active:          u.active          ?? true,
            updatedAt:       u.updatedAt       || null,
          };
          setProfileData(mapped);
          setEditedData(mapped);
        } else {
          setError('No se pudo cargar el perfil.');
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError('Error de conexión al cargar el perfil.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    return () => controller.abort();
  }, []);

  const handleEdit = () => {
    setEditedData(profileData);
    setSaveError('');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedData(profileData);
    setSaveError('');
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.profile, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName:     editedData.companyName,
          rut:             editedData.rut,
          address:         editedData.address,
          phoneNumber:     editedData.phoneNumber,
          contactName:     editedData.contactName,
          contactPosition: editedData.contactPosition,
          contactPhone:    editedData.contactPhone,
          contactEmail:    editedData.contactEmail,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const u = data.user;
        const updated: ProfileData = {
          companyName:     u.companyName     || '',
          rut:             u.rut             || '',
          address:         u.address         || '',
          phoneNumber:     u.phone           || '',
          email:           u.email           || '',
          contactName:     u.contactName     || '',
          contactPosition: u.contactPosition || '',
          contactPhone:    u.contactPhone    || '',
          contactEmail:    u.contactEmail    || '',
          active:          u.active          ?? true,
          updatedAt:       u.updatedAt       || null,
        };
        setProfileData(updated);
        setEditedData(updated);
        setIsEditing(false);
      } else {
        setSaveError(data.message || 'No se pudo guardar el perfil.');
      }
    } catch {
      setSaveError('Error de conexión al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ProfileData, value: string) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 flex items-center justify-center h-48">
        <p className="text-gray-500">Cargando perfil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const field = (
    key: keyof ProfileData,
    label: string,
    type: string = 'text',
    icon?: React.ReactNode
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-600 mb-1">
        {icon ? <span className="flex items-center gap-1">{icon}{label}</span> : label}
      </label>
      {isEditing && key !== 'email' ? (
        <input
          type={type}
          value={editedData[key] as string}
          onChange={(e) => handleChange(key, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      ) : (
        <p className={`text-gray-800 ${key === 'email' && isEditing ? 'font-medium' : ''}`}>
          {profileData[key] as string || '—'}
          {key === 'email' && isEditing && (
            <span className="ml-2 text-xs text-gray-400">(no editable)</span>
          )}
        </p>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Building2 className="text-blue-600" size={28} />
          Perfil del Centro Médico
        </h2>
        {!isEditing ? (
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
          >
            <Edit2 size={18} />
            Editar
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition disabled:opacity-50"
            >
              <X size={18} />
              Cancelar
            </button>
          </div>
        )}
      </div>

      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{saveError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Información del Centro */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
            Información del Centro
          </h3>
          {field('companyName', 'Nombre del Centro')}
          {field('rut', 'RUT')}
          {field('address', 'Dirección', 'text', <MapPin size={16} />)}
          {field('phoneNumber', 'Teléfono', 'text', <Phone size={16} />)}
          {field('email', 'Email', 'email', <Mail size={16} />)}
        </div>

        {/* Persona de Contacto */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
            Persona de Contacto
          </h3>
          {field('contactName', 'Nombre')}
          {field('contactPosition', 'Cargo')}
          {field('contactPhone', 'Teléfono de contacto', 'text', <Phone size={16} />)}
          {field('contactEmail', 'Correo de contacto', 'email', <Mail size={16} />)}

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Estado de la Cuenta</h4>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${profileData.active ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className={`font-medium ${profileData.active ? 'text-green-700' : 'text-red-700'}`}>
                {profileData.active ? 'Activa' : 'Inactiva'}
              </span>
            </div>
            {profileData.updatedAt && (
              <p className="text-sm text-gray-600 mt-2">
                Última actualización:{' '}
                {new Date(profileData.updatedAt).toLocaleDateString('es-CL')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSection;
